import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Database, Warning, CheckCircle } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-white" data-testid="reset-password-page">
      <div className="relative z-10 w-full max-w-sm rounded-sm border border-zinc-300 bg-white p-8">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <Database size={20} weight="bold" className="text-blue-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Reset Password</h1>
        </div>

        {done ? (
          <div
            className="flex items-center gap-2 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
            data-testid="reset-password-success-message"
          >
            <CheckCircle size={16} /> Password reset! Redirecting to sign in...
          </div>
        ) : !token ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <Warning size={16} /> Invalid reset link - no token found.
            </div>
            <Link to="/forgot-password" className="text-center text-sm text-blue-600 hover:underline">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="reset-password-form">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.05em] text-zinc-500">New Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm border-zinc-300"
                data-testid="reset-password-new-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirm" className="text-xs uppercase tracking-[0.05em] text-zinc-500">Confirm Password</Label>
              <Input
                id="confirm"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="rounded-sm border-zinc-300"
                data-testid="reset-password-confirm-input"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-password-error-message">
                <Warning size={16} /> {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="mt-2 rounded-sm bg-blue-600 text-white hover:bg-blue-700" data-testid="reset-password-submit-button">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
